/* eslint-disable react/prop-types */
import { useAuth } from "@/contexts/AuthContext";
import { useGetAllSalesItemsByLanguageId, useGetDynamicPropertiesForProduct } from "@/services/Product";
import { PropertyConfigDto, PropertyDto } from "@/types";
import { Divider, Grid } from "@mantine/core";
import { Ref, useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CreateOrderWizard } from "../helpers/createOrderHelpers";
import { CreateOrderProductStep } from "../helpers/createOrderHelpers";
import Group from "@/components/atoms/Group";
import Select from "@/components/atoms/Select";
import TextInput from "@/components/atoms/TextInput";
import SelectCurrency from "@/components/molecules/SelectInputs/SelectCurrency";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import { createProductValidationSchema } from "./Validation/ProductValidation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGetCreateOrderValues } from "@/services/Orders";

type PropertyField = {
  selectedValueId: string | null; // selected value for RHF
  key: string; // unique key (from property index)
  label: string;
  propertiesData: PropertyDto[];
  config: PropertyConfigDto;
};
export type CreateOrderProductFormValues = Omit<CreateOrderProductStep, "properties" | "salesItemId"> & {
  selectedSalesItemId: string;
  properties: PropertyField[] | null;
};

interface Props {
  onValidationSuccess: () => void;
  onChangeWizardData: (data: CreateOrderProductStep) => void;
  wizardData: CreateOrderWizard;
  ref?: Ref<HTMLFormElement>;
}

function Product({ onValidationSuccess, onChangeWizardData, wizardData, ref }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { data: salesItemsData, isLoading: isLoadingSalesItems } = useGetAllSalesItemsByLanguageId(
    user?.language.id as number,
    {
      enabled: !!user?.language.id,
    }
  );
  const { data: unitData } = useGetCreateOrderValues();

  const {
    control,
    watch,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<CreateOrderProductFormValues>({
    mode: "onSubmit",
    defaultValues: {
      amount: wizardData.amount,
      amountDeviation: wizardData.amountDeviation,
      unit: wizardData.unit,
      currency: wizardData.currency,
      minMaxPrice: wizardData.minMaxPrice,
      price: wizardData.price,
      priceAdjustPercentage: wizardData.priceAdjustPercentage,
      selectedSalesItemId: "",
      minimumAmount: wizardData.minimumAmount,
    },
    resolver: zodResolver(createProductValidationSchema(wizardData.orderType)),
  });

  const { fields } = useFieldArray({
    control,
    name: "properties",
  });

  const selectedSalesItemId = watch("selectedSalesItemId");

  const { data: productPropertiesData, isFetching: isFetchingProductProperties } = useGetDynamicPropertiesForProduct(
    selectedSalesItemId ? Number(selectedSalesItemId) : 0,
    user?.language.id as number,
    {
      enabled: !!selectedSalesItemId && user?.language.id !== undefined,
    }
  );

  useEffect(() => {
    if (!productPropertiesData?.length) return;

    reset({
      selectedSalesItemId: getValues("selectedSalesItemId"),
      properties: productPropertiesData.map((property) => {
        return {
          key: property.config.translatedLabel.toString(),
          selectedValueId: null,
          label: property.config.translatedLabel,
          propertiesData: property.properties,
          config: property.config,
        };
      }),
    });
  }, [productPropertiesData, reset, getValues]);

  const onSubmit = async (data: CreateOrderProductFormValues) => {
    const selectedPropertyValues =
      data.properties
        ?.filter((props) => props.selectedValueId !== null)
        .map((props) => props.propertiesData.find((option) => option.id.toString() === props.selectedValueId))
        .filter((val) => val !== undefined) ?? [];

    onChangeWizardData({
      properties: selectedPropertyValues,
      price: data.price,
      unit: data.unit,
      currency: data.currency,
      minMaxPrice: data.minMaxPrice,
      priceAdjustPercentage: data.priceAdjustPercentage,
      amount: data.amount,
      amountDeviation: data.amountDeviation ? data.amountDeviation : "",
      minimumAmount: wizardData.orderType === OrderTypeEnum.BUY ? data.minimumAmount : "",
      salesItemId: data.selectedSalesItemId,
    });
    onValidationSuccess();
  };

  return (
    <>
      <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
        <Grid>
          <Grid.Col span={6}>
            <Controller
              control={control}
              name="selectedSalesItemId"
              rules={{ required: t("validation:salesItemRequired") }}
              render={({ field: { onChange, value } }) => (
                <Select
                  label={t("product:productType")}
                  withAsterisk
                  loading={isLoadingSalesItems}
                  data={salesItemsData?.map((item) => ({
                    value: item.salesItemId.toString(),
                    label: item.salesItemTranslated,
                  }))}
                  value={value ?? null}
                  onChange={(selectedId) => {
                    onChange(selectedId);
                  }}
                  error={errors.selectedSalesItemId ? t("validation:" + errors.selectedSalesItemId.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          {fields?.map((field, index) => {
            return (
              <Grid.Col span={6} key={field.config.translatedLabel}>
                <Controller
                  control={control}
                  name={`properties.${index}.selectedValueId`}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      label={field.config.translatedLabel}
                      withAsterisk={field.config.required}
                      loading={isFetchingProductProperties}
                      data={field.propertiesData?.map((option) => ({
                        value: option.id.toString(),
                        label: option.value,
                      }))}
                      value={value ?? null}
                      onChange={onChange}
                      error={
                        errors.properties?.[index]?.selectedValueId
                          ? t("validation:" + errors.properties?.[index]?.selectedValueId?.message)
                          : undefined
                      }
                    />
                  )}
                />
              </Grid.Col>
            );
          })}
        </Grid>

        <Divider my="xl" />

        <Grid>
          <Grid.Col span={6}>
            <Group justify="flex-start" align="flex-start" wrap="nowrap" gap={0}>
              <Controller
                control={control}
                name="price"
                render={({ field: { onChange, value }, fieldState }) => (
                  <TextInput
                    label={t("common:price")}
                    withAsterisk
                    value={value}
                    onChange={onChange}
                    error={fieldState.error ? t("validation:" + fieldState.error?.message) : undefined}
                  />
                )}
              />
              <Controller
                control={control}
                name="currency"
                render={({ field: { onChange, value }, fieldState }) => (
                  <SelectCurrency
                    id="currency"
                    label={t("common:currency")}
                    withAsterisk
                    value={value}
                    valueKey="id"
                    labelKey="value"
                    onChange={onChange}
                    error={fieldState.error ? t("validation:" + fieldState.error?.message) : undefined}
                  />
                )}
              />
            </Group>
          </Grid.Col>

          <Grid.Col span={6}>
            <Group justify="flex-start" align="flex-start" wrap="nowrap" gap={0}>
              <Controller
                control={control}
                name="amount"
                render={({ field: { onChange, value }, fieldState }) => (
                  <TextInput
                    label={t("common:amount")}
                    withAsterisk
                    value={value}
                    onChange={onChange}
                    error={fieldState.error ? t("validation:" + fieldState.error?.message) : undefined}
                  />
                )}
              />
              <Controller
                control={control}
                name="amountDeviation"
                render={({ field: { onChange, value } }) => (
                  <Select
                    id="currency"
                    label={"+/-"}
                    value={value?.toString()}
                    data={[5, 10, 15, 20, 25, 30, 35, 40].map((num) => ({
                      value: num.toString(),
                      label: num + " %",
                    }))}
                    onChange={onChange}
                  />
                )}
              />
            </Group>
          </Grid.Col>

          <Grid.Col span={6}>
            <Controller
              control={control}
              name="unit"
              render={({ field: { onChange, value }, fieldState }) => (
                <Select
                  id="unit"
                  label={t("common:unit")}
                  withAsterisk
                  data={
                    unitData?.units?.map((unit) => ({
                      value: unit.id.toString(),
                      label: unit.value,
                    })) ?? []
                  }
                  value={value}
                  onChange={onChange}
                  error={fieldState.error ? t("validation:" + fieldState.error?.message) : undefined}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <Controller
              control={control}
              name="minMaxPrice"
              render={({ field: { onChange, value }, fieldState }) => (
                <TextInput
                  label={wizardData.orderType === OrderTypeEnum.SELL ? t("orders:minPrice") : t("orders:maxPrice")}
                  value={value}
                  onChange={onChange}
                  error={fieldState.error ? t("validation:" + fieldState.error?.message) : undefined}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <Controller
              control={control}
              name="priceAdjustPercentage"
              render={({ field: { onChange, value } }) => (
                <TextInput
                  label={` ${wizardData.orderType === OrderTypeEnum.SELL ? t("orders:minPrice") : t("orders:maxPrice")} %`}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid.Col>

          {wizardData.orderType === OrderTypeEnum.SELL && (
            <Grid.Col span={6}>
              <Controller
                control={control}
                name="minimumAmount"
                render={({ field: { onChange, value } }) => (
                  <TextInput label={t("dashboard:minimumAmount")} value={value?.toString()} onChange={onChange} />
                )}
              />
            </Grid.Col>
          )}
        </Grid>
      </form>
    </>
  );
}

export default Product;
